// items.ts - typed client for /api/v1/rooms/{roomId}/items endpoints.
//
// All item content is encrypted client-side before being sent. The
// server only ever sees ciphertext, nonce, and AAD.

export interface CreateItemRequest {
  itemId: string;     // client-generated, 26-char base32 (used for HKDF key derivation)
  kind: 'text' | 'link';
  nonce: string;      // base64 of 12-byte AES-GCM IV
  aad: string;        // base64 of additional authenticated data
  ciphertext: string; // base64 of AES-GCM ciphertext
}

export interface ItemResponse {
  itemId: string;
  roomId: string;
  kind: string;
  nonce: string;
  aad: string;
  ciphertext: string;
  ciphertextSize: number;
  blobId?: string; // present for kind="file" items
  createdAt: string;
  expiresAt: string;
}

export interface ListItemsResult {
  items: ItemResponse[];
  hasMore: boolean;
  nextAfter: string;
}

export async function createItem(
  roomId: string,
  accessToken: string,
  req: CreateItemRequest,
): Promise<ItemResponse> {
  const resp = await fetch(`/api/v1/rooms/${roomId}/items`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Room-Access-Token': accessToken,
    },
    body: JSON.stringify(req),
  });
  if (!resp.ok) throw await parseError(resp);
  return resp.json();
}

export async function listItems(
  roomId: string,
  accessToken: string,
  afterID?: string,
): Promise<ListItemsResult> {
  const url = new URL(`/api/v1/rooms/${roomId}/items`, window.location.origin);
  if (afterID) url.searchParams.set('after', afterID);
  const resp = await fetch(url.toString(), {
    headers: { 'X-Room-Access-Token': accessToken },
  });
  if (!resp.ok) throw await parseError(resp);
  return resp.json();
}

export async function getItem(
  roomId: string,
  itemId: string,
  accessToken: string,
): Promise<ItemResponse> {
  const resp = await fetch(`/api/v1/rooms/${roomId}/items/${itemId}`, {
    headers: { 'X-Room-Access-Token': accessToken },
  });
  if (!resp.ok) throw await parseError(resp);
  return resp.json();
}

export async function deleteItem(
  roomId: string,
  itemId: string,
  accessToken: string,
): Promise<void> {
  const resp = await fetch(`/api/v1/rooms/${roomId}/items/${itemId}`, {
    method: 'DELETE',
    headers: { 'X-Room-Access-Token': accessToken },
  });
  // 204 is success; 404 is idempotent success.
  if (!resp.ok && resp.status !== 404) throw await parseError(resp);
}

async function parseError(resp: Response): Promise<Error> {
  try {
    const body = await resp.json();
    const err = body.error;
    if (err && err.code) {
      return new ApiError(err.code, resp.status, err.requestId ?? '', err.message ?? 'unknown error');
    }
  } catch {
    // Non-JSON response.
  }
  return new ApiError('UNKNOWN', resp.status, '', `HTTP ${resp.status}`);
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly requestId: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}
